import {
  addMonths,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  isFriday,
  isSameMonth,
  isSameYear,
  parse,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { formatDate, formatDisplayDate } from '@/utils/date';
import { MONTH_PARAM_FORMAT, type MonthDayKind } from '../constants/holiday.constants';
import type { Holiday } from '../types/holiday.types';

const DAY_FORMAT = 'd';
const DAY_MONTH_FORMAT = 'd MMMM';
const FULL_DATE_FORMAT = 'd MMMM yyyy';

export function toMonthStart(monthParam: string | undefined): Date {
  if (monthParam === undefined) return startOfMonth(new Date());
  return parse(monthParam, MONTH_PARAM_FORMAT, new Date());
}

export function toMonthParam(monthStart: Date): string {
  return format(monthStart, MONTH_PARAM_FORMAT);
}

export function shiftMonth(monthStart: Date, monthCount: number): Date {
  return addMonths(monthStart, monthCount);
}

export function getHolidaysInMonth(holidays: Holiday[], monthStart: Date): Holiday[] {
  const firstDay = formatDate(monthStart);
  const lastDay = formatDate(endOfMonth(monthStart));
  return holidays.filter((holiday) => holiday.dateFrom <= lastDay && holiday.dateTo >= firstDay);
}

export function getHolidaysOnDay(holidays: Holiday[], isoDate: string): Holiday[] {
  return holidays.filter((holiday) => holiday.dateFrom <= isoDate && holiday.dateTo >= isoDate);
}

export function getMonthDayCounts(
  monthStart: Date,
  holidays: Holiday[],
): Record<MonthDayKind, number> {
  const activeHolidays = holidays.filter((holiday) => holiday.isActive);
  const counts: Record<MonthDayKind, number> = { working: 0, weekend: 0, holiday: 0 };
  for (const day of eachDayOfInterval({ start: monthStart, end: endOfMonth(monthStart) })) {
    if (getHolidaysOnDay(activeHolidays, formatDate(day)).length > 0) counts.holiday += 1;
    else if (isFriday(day)) counts.weekend += 1;
    else counts.working += 1;
  }
  return counts;
}

export function getHolidayDayCount(holiday: Holiday): number {
  return differenceInCalendarDays(parseISO(holiday.dateTo), parseISO(holiday.dateFrom)) + 1;
}

export function formatHolidayLongDates(holiday: Holiday): string {
  const startDate = parseISO(holiday.dateFrom);
  const endDate = parseISO(holiday.dateTo);
  if (holiday.dateFrom === holiday.dateTo) return format(startDate, FULL_DATE_FORMAT);
  if (isSameMonth(startDate, endDate)) {
    return `${format(startDate, DAY_FORMAT)} - ${format(endDate, FULL_DATE_FORMAT)}`;
  }
  if (isSameYear(startDate, endDate)) {
    return `${format(startDate, DAY_MONTH_FORMAT)} - ${format(endDate, FULL_DATE_FORMAT)}`;
  }
  return `${format(startDate, FULL_DATE_FORMAT)} - ${format(endDate, FULL_DATE_FORMAT)}`;
}

export function formatHolidayDates(holiday: Holiday): string {
  if (holiday.dateFrom === holiday.dateTo) return formatDisplayDate(holiday.dateFrom);
  return `${formatDisplayDate(holiday.dateFrom)} - ${formatDisplayDate(holiday.dateTo)}`;
}
